import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import client from '../api/client';
import { ERP_URL, AI_ASK_TIMEOUT_MS } from '../config';
import OrionToolCharts from '../components/OrionToolCharts';
import OrionMessageRenderer from '../components/OrionMessageRenderer';
import OrionQuickReplies from '../components/OrionQuickReplies';
import { ConfirmModal } from '../components/ConfirmModal';
import SupportTicketModal from '../components/SupportTicketModal';
import ProviderSettingsSidebar from '../components/ProviderSettingsSidebar';
import PackingListFilesModal from '../components/PackingListFilesModal';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { hapticLight } from '../utils/haptic';
import { resolveSlashCommand, getSlashCommandListForHelp, getPlaceholderSlashHint } from '../constants/slashCommands';

const SESSION_STORAGE_KEY = 'orion_ai_session_id';
const BRANCH_ID_KEY = 'branch_id';

const SUGGESTED_PROMPTS = [
  { labelKey: 'suggestedTodaySales', prompt: "Summarize today's sales: revenue, invoice count, and top items." },
  { labelKey: 'suggestedLowStock', prompt: 'Which items are low on stock? Show me inventory status and low-stock alerts.' },
  { labelKey: 'suggestedOpenTasks', prompt: 'What Orion tasks are open? List pending tasks and priorities.' },
  { labelKey: 'suggestedProfitability', prompt: 'What items are the most profitable? Which clients/suppliers are most profitable and why? Which items are losing money?' },
];

const AI_LIMIT_MSG = 'AI monthly limit reached';
const TENANT_UNAVAILABLE = 'Tenant database not available';

function toToolLabel(name) {
  return String(name || '').replace(/_/g, ' ').trim();
}

/** Strip markdown to plain text for copy. */
function markdownToPlain(md) {
  if (!md || typeof md !== 'string') return '';
  return md
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^[-*]\s+/gm, '• ')
    .replace(/^\d+\.\s+/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function isPlainObject(v) {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

/** Detect raw API/JSON leak so we never render it as message content. */
function isLikelyLeakedJson(text) {
  if (typeof text !== 'string' || !text.trim()) return false;
  const t = text.trim();
  if (!t.startsWith('{')) return false;
  return (
    t.includes('"type"') &&
    (t.includes('"action_required"') || t.includes('"blocks"') || t.includes('"summary"') || t.includes('"tool_results"') || t.includes('"quickReplies"') || t.includes('"oip"') || t.includes('"legacy"'))
  );
}

/** Use safe display text: prefer summary when content looks like leaked JSON. */
function safeMessageText(text, summaryFallback) {
  if (isLikelyLeakedJson(text)) return summaryFallback || '';
  return text || '';
}

function inferTable(value) {
  if (!Array.isArray(value) || value.length === 0) return null;
  const first = value[0];
  if (!isPlainObject(first)) return null;
  const keys = Array.from(new Set(value.flatMap((row) => (isPlainObject(row) ? Object.keys(row) : []))));
  if (keys.length === 0) return null;
  return { keys, rows: value };
}

function safePrettyJson(value, maxChars = 16000) {
  try {
    const s = JSON.stringify(value, null, 2);
    if (!s) return '';
    if (s.length <= maxChars) return s;
    return s.slice(0, Math.max(0, maxChars - 20)) + '\n…(truncated)';
  } catch {
    return String(value ?? '');
  }
}

function ToolResults({ toolResults, t, onCopy, locale }) {
  if (!Array.isArray(toolResults) || toolResults.length === 0) return null;
  const isAr = locale === 'ar';
  const openLabel = isAr ? 'فتح' : 'Open';

  const renderTable = (table, { sectionTitle } = {}) => {
    const hasItemId = table.keys.includes('item_id');
    const showOpenLink = hasItemId && ERP_URL;
    return (
    <div className="console-tool-section">
      {sectionTitle ? <div className="console-tool-section-title">{sectionTitle}</div> : null}
      <div className="console-tool-table-wrap">
        <table className="console-tool-table">
          <thead>
            <tr>
              {table.keys.map((k) => <th key={k}>{k}</th>)}
              {showOpenLink && <th>{openLabel}</th>}
            </tr>
          </thead>
          <tbody>
            {table.rows.slice(0, 15).map((row, ri) => (
              <tr key={ri}>
                {table.keys.map((k) => (
                  <td key={k}>{row?.[k] != null ? String(row[k]) : '—'}</td>
                ))}
                {showOpenLink && (
                  <td>
                    {row?.item_id != null ? (
                      <a href={`${ERP_URL}/inventory?item_id=${encodeURIComponent(row.item_id)}`} target="_blank" rel="noopener noreferrer" className="console-tool-open-link">
                        {openLabel}
                      </a>
                    ) : '—'}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {table.rows.length > 15 && (
          <div className="console-tool-hint">{t('showingFirstN', { count: 15, total: table.rows.length })}</div>
        )}
      </div>
    </div>
  );
  };

  const renderResult = (ok, result, error) => {
    if (!ok) {
      return (
        <div className="console-tool-section">
          <div className="console-tool-section-title">{t('result')}</div>
          <pre className="console-tool-pre">{safePrettyJson({ error }, 12000)}</pre>
        </div>
      );
    }

    const topLevelTable = inferTable(result);
    if (topLevelTable) return renderTable(topLevelTable);

    if (isPlainObject(result)) {
      const period = isPlainObject(result.period) ? result.period : null;
      const entries = Object.entries(result);
      return (
        <>
          {period && (
            <div className="console-tool-section">
              <div className="console-tool-section-title">{t('period')}</div>
              <div className="console-tool-kv">
                <span>{period.start ?? '—'}</span>
                <span>→</span>
                <span>{period.end ?? '—'}</span>
              </div>
            </div>
          )}
          {entries.map(([k, v]) => {
            if (k === 'period') return null;
            const tbl = inferTable(v);
            if (tbl) return <div key={k}>{renderTable(tbl, { sectionTitle: k })}</div>;
            if (v == null) return null;
            if (Array.isArray(v) && v.length === 0) return null;
            if (typeof v === 'string' && !v.trim()) return null;
            return (
              <div key={k} className="console-tool-section">
                <div className="console-tool-section-title">{k}</div>
                <pre className="console-tool-pre">{safePrettyJson(v, 12000)}</pre>
              </div>
            );
          })}
          <div className="console-tool-section">
            <div className="console-tool-section-title">{t('fullJson')}</div>
            <pre className="console-tool-pre">{safePrettyJson(result, 12000)}</pre>
          </div>
        </>
      );
    }

    return (
      <div className="console-tool-section">
        <div className="console-tool-section-title">{t('result')}</div>
        <pre className="console-tool-pre">{safePrettyJson(result, 12000) || t('noResult')}</pre>
      </div>
    );
  };

  return (
    <details className="console-tool-results">
      <summary className="console-tool-results-summary">
        <span className="console-tool-results-icon" aria-hidden>🧾</span>
        {t('toolDataResults')} <span className="console-tool-results-count">({toolResults.length})</span>
      </summary>
      <div className="console-tool-results-list">
        {toolResults.map((tr, idx) => {
          const name = tr?.toolName ?? tr?.tool ?? tr?.name ?? '';
          const ok = tr?.ok !== false && tr?.success !== false && !tr?.error;
          const args = tr?.args;
          const result = tr?.result;
          const error = tr?.error;
          return (
            <details key={`${String(name)}-${idx}`} className={`console-tool-result ${ok ? 'console-tool-result-ok' : 'console-tool-result-fail'}`}>
              <summary className="console-tool-result-summary">
                <span className="console-tool-result-status" aria-hidden>{ok ? '✅' : '❌'}</span>
                <span className="console-tool-result-name">{toToolLabel(name) || t('tool')}</span>
              </summary>
              {args ? (
                <div className="console-tool-section">
                  <div className="console-tool-section-title">{t('args')}</div>
                  <pre className="console-tool-pre">{safePrettyJson(args, 6000)}</pre>
                </div>
              ) : null}
              {result && tr.chartHint ? (
                <div className="console-tool-section console-tool-charts-wrap">
                  <OrionToolCharts result={result} chartHint={tr.chartHint} />
                </div>
              ) : null}
              {renderResult(ok, result, error)}
              <div className="console-tool-actions">
                <button
                  type="button"
                  className="console-btn console-tool-btn"
                  onClick={() => {
                    const txt = safePrettyJson(ok ? result : { error }, 50000);
                    if (txt && navigator.clipboard?.writeText) {
                      navigator.clipboard.writeText(txt);
                      onCopy?.();
                    }
                  }}
                >
                  {t('copyJson')}
                </button>
              </div>
            </details>
          );
        })}
      </div>
    </details>
  );
}

function getOrCreateSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (id && id.trim().length >= 8) return id.trim();
    id = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `orion-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    sessionStorage.setItem(SESSION_STORAGE_KEY, id);
    return id;
  } catch {
    return `orion-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }
}

function newSessionId() {
  const id = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `orion-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  sessionStorage.setItem(SESSION_STORAGE_KEY, id);
  return id;
}

function toErrorString(val) {
  if (val == null) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'object' && typeof val.message === 'string') return val.message;
  if (typeof val === 'object' && typeof val.userSummary === 'string') return val.userSummary;
  try {
    return JSON.stringify(val);
  } catch {
    return String(val);
  }
}

function normalizeErrorMessage(err, t) {
  const status = err.response?.status;
  const raw = err.response?.data?.message ?? err.response?.data?.error ?? err.message ?? 'Request failed';
  const msg = toErrorString(raw);
  if (status === 429) {
    if (msg.includes(AI_LIMIT_MSG)) {
      return t ? t('aiLimitReached') : 'AI monthly limit reached. Please try again later or contact support.';
    }
    return t ? t('tooManyRequests') : 'Too many requests. Please wait a minute before sending again.';
  }
  if (msg.includes(AI_LIMIT_MSG)) {
    return t ? t('aiLimitReached') : 'AI monthly limit reached. Please try again later or contact support.';
  }
  if (status === 503 || (msg && msg.toLowerCase().includes(TENANT_UNAVAILABLE.toLowerCase()))) {
    return t ? t('serviceUnavailable') : 'Service is temporarily unavailable. Please try again in a moment.';
  }
  return msg;
}

const MAX_MESSAGES = 100;

/** Exponential backoff sleep (ms). */
async function sleep(ms) { return new Promise((r) => setTimeout(r, ms)); }

/**
 * If the API returns a raw action_required shape (type, blocks, message, input.options) without
 * data.oip, build a minimal OIP so OrionMessageRenderer can show the card and buttons instead of raw JSON.
 * Display is always driven by oip; legacy (tool_results, preview, etc.) is for extras only, not the main bubble.
 * @param {Object} data - response data (data.oip may be null)
 * @returns {Object|null} oip to use, or null if not applicable
 */
function oipFromRawResponse(data) {
  if (!data || typeof data !== 'object' || data.oip != null) return null;
  const type = data.type;
  if (type !== 'action_required' && type !== 'clarification') return null;
  const block = Array.isArray(data.blocks) && data.blocks.length > 0 ? data.blocks[0] : null;
  const summary = data.summary ?? data.message ?? block?.message ?? '';
  const message = data.message ?? block?.message ?? summary;
  const options = block?.input?.options ?? data.input?.options ?? block?.actions ?? data.actions ?? data.quickReplies;
  const actions = Array.isArray(options) && options.length > 0
    ? options.map((a, i) => {
        const id = (a.value ?? a.id ?? ((a.label || '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || `action-${i}`)).toString();
        const label = (a.label ?? a.labelAr ?? id).toString().trim();
        return { id, label, labelAr: a.labelAr, variant: id === 'confirm' || id === 'approve' ? 'primary' : 'secondary' };
      })
    : [{ id: 'approve', label: 'Confirm', variant: 'primary' }, { id: 'cancel', label: 'Cancel', variant: 'secondary' }];
  return {
    type: 'action_required',
    summary: String(summary).slice(0, 2048),
    message: String(message).slice(0, 2048),
    actions,
    meta: data.meta && typeof data.meta === 'object' ? data.meta : {},
  };
}

/**
 * Post to /api/ai/ask with automatic retry for 429 / 503 responses.
 * Uses longer backoff for 429 to avoid hammering the server.
 * Accepts an AbortSignal so the caller can cancel mid-retry.
 */
async function postAskWithRetry(payload, config, maxRetries = 2) {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await client.post('/api/ai/ask', payload, config);
    } catch (err) {
      if (config?.signal?.aborted) throw err; // respect cancellation
      const status = err?.response?.status;
      const isRetryable = status === 429 || status === 503;
      if (isRetryable && attempt < maxRetries) {
        // 429: use longer backoff (2s, 4s) so we don't trigger more rate limits
        const baseMs = status === 429 ? 2000 : 1000;
        await sleep(baseMs * Math.pow(2, attempt));
      } else {
        throw err;
      }
    }
  }
}

export default function ConsolePage() {
  const { locale, setLocale, dir, lang, t } = useLocale();
  const [messages, setMessages] = useState([]);
  const messagesRef = useRef([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const abortControllerRef = useRef(null);
  const [error, setError] = useState(null);
  const [, setSessionId] = useState(getOrCreateSessionId);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchIdState] = useState(() => localStorage.getItem(BRANCH_ID_KEY) || '');
  const [showScrollFab, setShowScrollFab] = useState(false);
  const [offline, setOffline] = useState(() => !navigator.onLine);
  const [announcer, setAnnouncer] = useState('');
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [listening, setListening] = useState(false);
  const [confirmNewChatOpen, setConfirmNewChatOpen] = useState(false);
  const [confirmLogoutOpen, setConfirmLogoutOpen] = useState(false);
  const [lastFailedPayload, setLastFailedPayload] = useState(null);
  const [hasNewMessageBelow, setHasNewMessageBelow] = useState(false);
  const [showSupportTicketModal, setShowSupportTicketModal] = useState(false);
  const [showSettingsSidebar, setShowSettingsSidebar] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [openBasedOnIndex, setOpenBasedOnIndex] = useState(null);
  // eslint-disable-next-line no-unused-vars -- setSessionContext used for side effects
  const [_sessionContext, setSessionContext] = useState(null);
  const [creativeMode, setCreativeMode] = useState(false);
  const [loadingQuickReplyId, setLoadingQuickReplyId] = useState(null);
  const [showPackingListModal, setShowPackingListModal] = useState(false);
  const [headerUsage, setHeaderUsage] = useState(null);
  const [showScheduled, setShowScheduled] = useState(false);
  const [scheduledList, setScheduledList] = useState([]);
  const [scheduledQuestionText, setScheduledQuestionText] = useState('');
  const [scheduledRunAt, setScheduledRunAt] = useState('');
  const [scheduling, setScheduling] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesScrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const headerMenuBtnRef = useRef(null);
  const headerMenuPanelRef = useRef(null);
  /** Per-session file workspace so follow-up messages (e.g. "نعم") still send packingListFileId for import */
  const sessionWorkspaceRef = useRef({});
  const pendingQuickReplyIdRef = useRef(null);
  const { user, logout } = useAuth();

  useFocusTrap(headerMenuOpen, headerMenuPanelRef, headerMenuBtnRef);

  useEffect(() => {
    if (!headerMenuOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setHeaderMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [headerMenuOpen]);

  useEffect(() => {
    if (!showSettingsSidebar) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setShowSettingsSidebar(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showSettingsSidebar]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const setBranchId = useCallback((id) => {
    const val = id != null ? String(id) : '';
    setBranchIdState(val);
    if (val) localStorage.setItem(BRANCH_ID_KEY, val);
    else localStorage.removeItem(BRANCH_ID_KEY);
    setSessions([]);
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setShowScrollFab(false);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    const last = messages[messages.length - 1];
    if (last?.role !== 'assistant') return;
    requestAnimationFrame(() => {
      const el = messagesScrollRef.current;
      if (!el) return;
      const { scrollTop, scrollHeight, clientHeight } = el;
      if (scrollHeight - scrollTop - clientHeight >= 80) setHasNewMessageBelow(true);
    });
  }, [messages]);

  useEffect(() => {
    document.title = user ? 'Orion Console' : 'Orion';
  }, [user]);

  useEffect(() => {
    const onScroll = () => {
      const el = messagesScrollRef.current;
      if (!el) return;
      const { scrollTop, scrollHeight, clientHeight } = el;
      const nearBottom = scrollHeight - scrollTop - clientHeight < 80;
      setShowScrollFab(!nearBottom && scrollHeight - scrollTop - clientHeight > 120);
      if (nearBottom) setHasNewMessageBelow(false);
    };
    const el = messagesScrollRef.current;
    el?.addEventListener('scroll', onScroll);
    return () => el?.removeEventListener('scroll', onScroll);
  }, [messages.length]);

  useEffect(() => {
    const handleOnline = () => setOffline(false);
    const handleOffline = () => setOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const fetchBranches = useCallback(async () => {
    try {
      const res = await client.get('/branches', { timeout: 8000 });
      const list = Array.isArray(res.data) ? res.data : [];
      setBranches(list);
      if (list.length > 0 && !localStorage.getItem(BRANCH_ID_KEY)) {
        const defaultBranch = list.find((b) => b.is_default) || list[0];
        if (defaultBranch?.id != null) {
          setBranchId(defaultBranch.id);
        }
      }
    } catch {
      setBranches([]);
    }
  }, [setBranchId]);

  const fetchSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const res = await client.get('/api/ai/sessions', { params: { limit: 30 }, timeout: 8000 });
      setSessions(res.data?.sessions ?? []);
    } catch {
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  useEffect(() => {
    if (messages.length > 0) return;
    let cancelled = false;
    client.get('/api/ai/session-context', { timeout: 8000 })
      .then((r) => {
        if (cancelled) return;
        const data = r.data ?? {};
        setSessionContext(data);
        const { brief, insightCount = 0, taskCount = 0 } = data;
        const hasActionable = insightCount > 0 || taskCount > 0;
        if (brief && hasActionable) {
          setMessages((prev) => (prev.length === 0 ? [{ role: 'assistant', text: `📊 ${brief}`, _brief: true }] : prev));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [messages.length]);

  useEffect(() => {
    if (showHistory) fetchSessions();
  }, [showHistory, fetchSessions]);

  useEffect(() => {
    let cancelled = false;
    client.get('/api/ai/usage', { timeout: 8000 })
      .then((r) => { if (!cancelled) setHeaderUsage(r.data); })
      .catch(() => { if (!cancelled) setHeaderUsage(null); });
    return () => { cancelled = true; };
  }, [messages.length]);

  useEffect(() => {
    if (!showScheduled) return;
    client.get('/api/ai/scheduled-questions', { timeout: 8000 })
      .then((r) => setScheduledList(r.data?.items ?? []))
      .catch(() => setScheduledList([]));
  }, [showScheduled]);

  const loadConversation = useCallback(async (sid) => {
    try {
      const res = await client.get('/api/ai/conversation', {
        params: { sessionId: sid },
        headers: { 'X-AI-Session-Id': sid },
        timeout: 8000,
      });
      const raw = res.data?.messages ?? [];
      const msgs = raw.map((m) => ({ role: m.role || 'user', text: m.content || m.text || '' }));
      setMessages(msgs);
      setSessionId(sid);
      sessionStorage.setItem(SESSION_STORAGE_KEY, sid);
      setShowHistory(false);
      setError(null);
    } catch {
      setError(t('couldNotLoadConversation'));
    }
  }, [t]);

  const hasRestoredRef = useRef(false);
  useEffect(() => {
    if (hasRestoredRef.current || messages.length > 0) return;
    const sid = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (sid && sid.trim().length >= 8) {
      hasRestoredRef.current = true;
      loadConversation(sid);
    }
  }, [loadConversation, messages.length]);

  const handleNewChat = () => {
    setConfirmNewChatOpen(true);
  };
  const doNewChat = () => {
    setMessages([]);
    setError(null);
    setLastFailedPayload(null);
    setSessionId(newSessionId());
    setShowHistory(false);
    setConfirmNewChatOpen(false);
    sessionWorkspaceRef.current = {};
  };

  const handleExportPdf = useCallback(() => {
    if (messages.length === 0) {
      setAnnouncer(t('noMessagesToExport'));
      return;
    }
    try {
      const { jsPDF } = require('jspdf');
      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const margin = 40;
      let y = 40;
      doc.setFontSize(14);
      doc.text('ORION Chat Export', margin, y);
      y += 24;
      doc.setFontSize(10);
      doc.text(`Exported: ${new Date().toLocaleString()}`, margin, y);
      y += 20;
      for (const msg of messages) {
        if (y > 700) { doc.addPage(); y = 40; }
        doc.setFontSize(10);
        doc.setFont(undefined, msg.role === 'assistant' ? 'normal' : 'bold');
        doc.text(`${msg.role === 'assistant' ? 'ORION' : 'You'}:`, margin, y);
        y += 14;
        const text = (msg.text || '').replace(/\n/g, '\n');
        const lines = doc.splitTextToSize(text, doc.internal.pageSize.getWidth() - margin * 2);
        doc.setFont(undefined, 'normal');
        for (const line of lines) {
          if (y > 700) { doc.addPage(); y = 40; }
          doc.text(line, margin, y);
          y += 12;
        }
        if (msg.role === 'assistant' && (msg.toolsUsed?.length > 0 || msg.basedOn)) {
          const toolsLine = msg.toolsUsed?.length ? `Tools: ${msg.toolsUsed.join(', ')}` : '';
          const basedLine = msg.basedOn ? `Based on: ${[msg.basedOn.branchName, msg.basedOn.dateRange?.from, msg.basedOn.dateRange?.to].filter(Boolean).join(' · ')}` : '';
          const meta = [toolsLine, basedLine].filter(Boolean).join(' | ');
          if (meta && y <= 700) {
            y += 4;
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            const metaLines = doc.splitTextToSize(meta, doc.internal.pageSize.getWidth() - margin * 2);
            for (const line of metaLines) {
              if (y > 700) { doc.addPage(); y = 40; }
              doc.text(line, margin, y);
              y += 10;
            }
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(10);
          }
        }
        y += 8;
      }
      doc.save(`orion-chat-${new Date().toISOString().slice(0, 10)}.pdf`);
      setAnnouncer(t('pdfDownloaded'));
      setExportMenuOpen(false);
    } catch (e) {
      setError(toErrorString(e?.message ?? e) || t('exportFailed'));
    }
  }, [messages, t]);

  const handleExportExcel = useCallback(() => {
    if (messages.length === 0) {
      setAnnouncer(t('noMessagesToExport'));
      return;
    }
    try {
      const XLSX = require('xlsx');
      const data = messages.map((m) => ({
        Role: m.role === 'assistant' ? 'ORION' : 'User',
        Content: (m.text || '').slice(0, 32000),
        'Tools used': m.role === 'assistant' && m.toolsUsed?.length ? m.toolsUsed.join(', ') : '',
        'Based on': m.role === 'assistant' && m.basedOn
          ? [m.basedOn.branchName, m.basedOn.dateRange?.from, m.basedOn.dateRange?.to].filter(Boolean).join(' · ')
          : '',
        Timestamp: m.timestamp ? new Date(m.timestamp).toLocaleString() : '',
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Chat');
      XLSX.writeFile(wb, `orion-chat-${new Date().toISOString().slice(0, 10)}.xlsx`);
      setAnnouncer(t('excelDownloaded'));
      setExportMenuOpen(false);
    } catch (e) {
      setError(toErrorString(e?.message ?? e) || t('exportFailed'));
    }
  }, [messages, t]);

  /** OIP approval: POST /api/orion/approve. When executionId is missing (e.g. oip built from raw), show friendly message instead of calling API. */
  const handleOipApprove = useCallback(async (msgIndex, executionId, decision) => {
    if (!executionId) {
      setAnnouncer(t('approvalMissingExecutionId'));
      return;
    }
    setLoading(true);
    try {
      const sid = getOrCreateSessionId();
      const res = await client.post('/api/orion/approve', { executionId, decision }, {
        timeout: 30000,
        headers: { 'X-AI-Session-Id': sid },
      });
      const data = res.data ?? {};
      const oip = data.oip ?? null;
      const resultText = oip?.type === 'chat' ? (oip.message ?? '') : (decision === 'cancel' ? t('operationCancelled') : t('operationCompletedShort'));
      setMessages((prev) => {
        const next = [...prev];
        if (typeof msgIndex === 'number' && next[msgIndex]) {
          next[msgIndex] = { ...next[msgIndex], oip: oip || next[msgIndex].oip, text: resultText };
        }
        if (oip && decision === 'approve') next.push({ role: 'assistant', oip, text: resultText });
        return next;
      });
      setAnnouncer(decision === 'cancel' ? t('operationCancelled') : t('actionExecuted'));
    } catch (err) {
      const status = err?.response?.status;
      const msg = status === 404 ? t('approvalExpired') : normalizeErrorMessage(err, t);
      setAnnouncer(status === 404 ? t('approvalExpired') : msg);
      if (status !== 404) setError(msg);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [t]);

  const executeApproved = useCallback(async ({ toolName, args }, { msgIndex } = {}) => {
    if (!toolName) return;
    setLoading(true);
    try {
      const sid = getOrCreateSessionId();
      const res = await client.post('/api/ai/execute-approved', { toolName, args }, { timeout: 60000, headers: { 'X-AI-Session-Id': sid } });
      const data = res.data ?? {};
      const result = data.legacy?.result ?? data.result;
      const resultText = data.oip?.type === 'chat' ? (data.oip.message ?? '') : (result?.message ?? (result?.success !== false ? t('operationCompleted') : result?.error ?? t('operationCompletedShort')));
      setMessages((prev) => {
        const next = [...prev];
        if (typeof msgIndex === 'number' && next[msgIndex]?.approvalPayload) {
          next[msgIndex] = { ...next[msgIndex], approvalPayload: { ...next[msgIndex].approvalPayload, status: 'approved' } };
        }
        next.push({ role: 'assistant', text: resultText });
        return next;
      });
      setAnnouncer(t('actionExecuted'));
    } catch (err) {
      const errMsg = normalizeErrorMessage(err, t);
      setError(errMsg);
      setMessages((prev) => [...prev, { role: 'assistant', text: t('operationFailed', { message: errMsg }) }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [t]);

  const retryDuplicateAsk = useCallback(async ({ messages: retryMessages, sessionId: sid }) => {
    if (!Array.isArray(retryMessages) || retryMessages.length === 0 || loading) return;
    setError(null);
    setLoading(true);
    setAnnouncer(t('retrying'));
    try {
      const session = sid || getOrCreateSessionId();
      const res = await client.post('/api/ai/ask', { messages: retryMessages, forceRetry: true, legacy: true }, { timeout: AI_ASK_TIMEOUT_MS, headers: { 'X-AI-Session-Id': session } });
      const data = res.data ?? {};
      const legacy = data.legacy ?? {};
      let oip = data.oip ?? null;
      if (!oip) {
        oip = oipFromRawResponse(data);
        if (oip && typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
          console.warn('[Orion Console] Response had no data.oip; normalized from raw shape. Prefer fixing backend to always send oip.');
        }
      }
      const replyLegacy = legacy.text ?? legacy.message ?? data.text ?? data.reply ?? data.message ?? data.response ?? data.answer ?? '';
      const isLegacyLeak = !oip && data.type === 'success' && (data.summary != null || Array.isArray(legacy.tool_results ?? data.tool_results));
      if (isLegacyLeak) {
        const summary = typeof data.summary === 'string' ? data.summary : '';
        oip = { type: 'chat', message: summary || safeMessageText(replyLegacy, summary) || t('noResponse') };
      }
      const oipIsLegacyDsl = oip && (oip.type === 'success' || (oip.type !== 'chat' && oip.type !== 'action_required' && oip.type !== 'error' && (oip.tool_results != null || oip.quickReplies != null)));
      if (oipIsLegacyDsl) {
        const summary = typeof oip.summary === 'string' ? oip.summary : (typeof oip.message === 'string' ? oip.message : '');
        oip = { type: 'chat', message: summary || safeMessageText(replyLegacy, summary) || t('noResponse') };
      }
      const msgText = oip?.type === 'chat' ? (oip.message ?? '') : (oip ? '' : (safeMessageText(replyLegacy, null) || t('noResponse')));
      const respSessionId = res.headers?.['x-ai-session-id'] ?? res.headers?.['X-AI-Session-Id'];
      if (respSessionId) {
        sessionStorage.setItem(SESSION_STORAGE_KEY, respSessionId);
        setSessionId(respSessionId);
      }

      const requiresApproval = legacy.requiresApproval ?? data?.requiresApproval;
      const approvalToolName = legacy.toolName ?? data?.toolName;
      const approvalPreview = legacy.preview ?? data?.preview;
      if (requiresApproval && approvalToolName && approvalPreview) {
        const summary = typeof approvalPreview === 'string' ? approvalPreview : approvalPreview?.summary ?? t('actionRequiresApproval');
        const toolArgs = legacy.args ?? data.args ?? data.toolArgs ?? data.preview?.data ?? {};
        const approvalMsgText = oip?.type === 'chat' ? (oip.message ?? '') : (summary || safeMessageText(replyLegacy, summary));
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            text: approvalMsgText,
            oip: oip,
            basedOn: legacy.based_on ?? data.based_on ?? null,
            toolsUsed: legacy.tools_used ?? data.tools_used ?? null,
            toolResults: Array.isArray(legacy.tool_results) ? legacy.tool_results : (Array.isArray(data.tool_results) ? data.tool_results : null),
            approvalPayload: { status: 'pending', toolName: approvalToolName, preview: approvalPreview, args: toolArgs },
          ...(Array.isArray(data?.legacy?.quickReplies ?? data?.quickReplies) && (data?.legacy?.quickReplies ?? data?.quickReplies).length > 0 ? { quickReplies: (data?.legacy?.quickReplies ?? data?.quickReplies) } : {}),
          },
        ]);
        setAnnouncer(t('approvalRequired'));
        return;
      }

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: msgText,
          oip: oip,
          basedOn: legacy.based_on ?? data.based_on ?? null,
          toolsUsed: legacy.tools_used ?? data.tools_used ?? null,
          toolResults: Array.isArray(legacy.tool_results) ? legacy.tool_results : (Array.isArray(data.tool_results) ? data.tool_results : null),
        },
      ]);
      setAnnouncer(t('orionReplied'));
    } catch (err) {
      const errMsg = normalizeErrorMessage(err, t);
      setError(errMsg);
      setMessages((prev) => [...prev, { role: 'assistant', text: t('retryFailed', { message: errMsg }) }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [loading, t]);

  const sendAsk = useCallback(async ({ text, forceRetry = false } = {}) => {
    const clean = String(text || '').trim();
    const hasAttachments = attachments.length > 0;
    if ((!clean && !hasAttachments) || loading) return;

    setError(null);
    setLastFailedPayload(null);
    const userMessage = { role: 'user', text: clean || (hasAttachments ? t('attachedFiles') : '') };
    setInput('');
    setMessages((prev) => {
      const next = [...prev, userMessage];
      return next.length > MAX_MESSAGES ? next.slice(-MAX_MESSAGES) : next;
    });
    setLoading(true);
    setAnnouncer(t('messageSent'));

    // Cancel any in-flight request before starting a new one.
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const sid = getOrCreateSessionId();
    const resolvedText = clean ? resolveSlashCommand(clean) : (userMessage.text || '');
    const userMessageForApi = { ...userMessage, text: resolvedText };
    const current = messagesRef.current || [];
    const messagesToSend = [...current, userMessageForApi].map((m) => ({ role: m.role, text: m.text }));

    // Cap history so we never send an unbounded context window.
    const cappedMessages = messagesToSend.slice(-MAX_MESSAGES);

    // Merge current attachments into session workspace so follow-up messages (e.g. "نعم") still send packingListFileId.
    const workspace = sessionWorkspaceRef.current[sid] || [];
    const seen = new Set(workspace.map((a) => a.fileId));
    for (const a of attachments) {
      if (!seen.has(a.fileId)) {
        seen.add(a.fileId);
        workspace.push(a);
      }
    }
    const MAX_SESSION_FILES = 10;
    const trimmed = workspace.slice(-MAX_SESSION_FILES);
    sessionWorkspaceRef.current[sid] = trimmed;

    const attachmentsPayload = trimmed.length
      ? trimmed.map((a) => ({
          fileId: a.fileId,
          type: a.type,
          name: a.name,
          ...(a.dataUrl ? { dataUrl: a.dataUrl } : {}),
          ...(a.textContent ? { textContent: a.textContent } : {}),
          ...(a.packingListFileId ? { packingListFileId: a.packingListFileId } : {}),
        }))
      : undefined;

    try {
      const maxQuickReplies = typeof window !== 'undefined' && window.innerWidth < 480 ? 3 : undefined;
      const quickReplyId = pendingQuickReplyIdRef.current ?? undefined;
      if (pendingQuickReplyIdRef.current) pendingQuickReplyIdRef.current = null;
      const res = await postAskWithRetry(
        {
          messages: cappedMessages,
          legacy: true,
          ...(forceRetry ? { forceRetry: true } : {}),
          ...(attachmentsPayload?.length ? { attachments: attachmentsPayload } : {}),
          ...(creativeMode ? { forceLlm: true } : {}),
          ...(quickReplyId ? { quickReplyId } : {}),
          ...(maxQuickReplies != null ? { maxQuickReplies } : {}),
        },
        { timeout: AI_ASK_TIMEOUT_MS, signal: controller.signal, headers: { 'X-AI-Session-Id': sid } }
      );
      const data = res.data ?? {};
      const legacy = data.legacy ?? {};
      let oip = data.oip ?? null;
      if (!oip) {
        oip = oipFromRawResponse(data);
        if (oip && typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
          console.warn('[Orion Console] Response had no data.oip; normalized from raw shape. Prefer fixing backend to always send oip.');
        }
      }
      const replyLegacy = legacy.text ?? legacy.message ?? data.text ?? data.reply ?? data.message ?? data.response ?? data.answer ?? '';
      // Guard: if backend ever returns legacy tool-DSL (type/summary/tool_results) without oip, treat as chat using summary
      const isLegacyLeak = !oip && data.type === 'success' && (data.summary != null || Array.isArray(legacy.tool_results ?? data.tool_results));
      if (isLegacyLeak) {
        const summary = typeof data.summary === 'string' ? data.summary : '';
        oip = { type: 'chat', message: summary || safeMessageText(replyLegacy, summary) || t('noResponse') };
      }
      // Guard: if oip is present but is legacy tool-DSL (type 'success' or has tool_results at top level), normalize to chat so we never render raw JSON
      const oipIsLegacyDsl = oip && (oip.type === 'success' || (oip.type !== 'chat' && oip.type !== 'action_required' && oip.type !== 'error' && (oip.tool_results != null || oip.quickReplies != null)));
      if (oipIsLegacyDsl) {
        const summary = typeof oip.summary === 'string' ? oip.summary : (typeof oip.message === 'string' ? oip.message : '');
        oip = { type: 'chat', message: summary || safeMessageText(replyLegacy, summary) || t('noResponse') };
      }
      const msgText = oip?.type === 'chat' ? (oip.message ?? '') : (oip ? '' : (safeMessageText(replyLegacy, null) || t('noResponse')));
      const respSessionId = res.headers?.['x-ai-session-id'] ?? res.headers?.['X-AI-Session-Id'];
      if (respSessionId) {
        sessionStorage.setItem(SESSION_STORAGE_KEY, respSessionId);
        setSessionId(respSessionId);
      }

      // Duplicate action flow: offer retry
      if (legacy.duplicate ?? data?.duplicate) {
        setAttachments([]);
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            text: legacy.message ?? (data.message || t('thisActionJustExecuted')),
            duplicatePayload: { messages: messagesToSend, sessionId: sid },
          },
        ]);
        return;
      }

      // Approval flow: show preview + actions (need toolName and preview)
      const requiresApproval = legacy.requiresApproval ?? data?.requiresApproval;
      const approvalToolName = legacy.toolName ?? data?.toolName;
      const approvalPreview = legacy.preview ?? data?.preview;
      if (requiresApproval && approvalToolName && approvalPreview) {
        const summary = typeof approvalPreview === 'string' ? approvalPreview : approvalPreview?.summary ?? t('actionRequiresApproval');
        const toolArgs = legacy.args ?? data.args ?? data.toolArgs ?? data.preview?.data ?? {};
        const approvalMsgText = oip?.type === 'chat' ? (oip.message ?? '') : (summary || safeMessageText(replyLegacy, summary));
        setAttachments([]);
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            text: approvalMsgText,
            oip: oip,
            basedOn: legacy.based_on ?? data.based_on ?? null,
            toolsUsed: legacy.tools_used ?? data.tools_used ?? null,
            toolResults: Array.isArray(legacy.tool_results) ? legacy.tool_results : (Array.isArray(data.tool_results) ? data.tool_results : null),
            approvalPayload: { status: 'pending', toolName: approvalToolName, preview: approvalPreview, args: toolArgs },
          },
        ]);
        setAnnouncer(t('approvalRequired'));
        return;
      }

      const quickReplies = data?.legacy?.quickReplies ?? data?.quickReplies ?? legacy?.quickReplies ?? null;
      setAttachments([]);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: msgText,
          oip: oip,
          basedOn: legacy.based_on ?? data.based_on ?? null,
          toolsUsed: legacy.tools_used ?? data.tools_used ?? null,
          toolResults: Array.isArray(legacy.tool_results) ? legacy.tool_results : (Array.isArray(data.tool_results) ? data.tool_results : null),
          ...(Array.isArray(quickReplies) && quickReplies.length > 0 ? { quickReplies } : {}),
        },
      ]);
      setAnnouncer(t('orionReplied'));
      hapticLight();
    } catch (err) {
      // Ignore abort errors — they happen when the user sends a new message
      // before the previous one completes.
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;

      if (err?.response?.status === 400 && process.env.NODE_ENV === 'development') {
        const d = err?.response?.data;
        console.warn('[Orion] 400:', d?.error?.message, 'details:', d?.error?.details, 'fields:', d?.error?.fields);
      }
      const errMsg = normalizeErrorMessage(err, t);
      setError(errMsg);
      setLastFailedPayload({ text: clean });
      setMessages((prev) => prev.slice(0, -1));
      if (clean) setInput(clean);
    } finally {
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
      setLoading(false);
      setLoadingQuickReplyId(null);
      inputRef.current?.focus();
    }
  }, [loading, t, attachments, creativeMode]);

  const handleSend = useCallback(async (e, optionalText) => {
    e?.preventDefault();
    hapticLight();
    const text = (optionalText ?? input ?? '').trim();
    await sendAsk({ text });
  }, [input, sendAsk]);

  const handleSuggestedPrompt = useCallback((prompt) => {
    if (loading) return;
    sendAsk({ text: prompt });
  }, [loading, sendAsk]);

  const handleQuickReply = useCallback((payload) => {
    if (loading) return;
    const prompt = payload?.prompt ?? payload?.text ?? '';
    if (!prompt) return;
    if (payload?.quickReplyId) {
      pendingQuickReplyIdRef.current = payload.quickReplyId;
      setLoadingQuickReplyId(payload.quickReplyId);
    }
    sendAsk({ text: prompt });
  }, [loading, sendAsk]);

  const handleCopyConversation = useCallback(() => {
    if (messages.length === 0) {
      setAnnouncer(t('noMessagesToExport'));
      return;
    }
    const lines = messages.map((m) => {
      const label = m.role === 'user' ? (locale === 'ar' ? 'أنت' : 'You') : 'ORION';
      const text = (m.text || '').trim();
      return `**${label}:**\n${text}`;
    });
    const md = lines.join('\n\n');
    if (navigator.clipboard?.writeText(md)) {
      navigator.clipboard.writeText(md).then(() => {
        setAnnouncer(t('copied'));
        setExportMenuOpen(false);
      }).catch(() => {});
    }
  }, [messages, locale, t]);

  const handleScheduleQuestion = useCallback(async () => {
    const text = (scheduledQuestionText || '').trim();
    const runAt = (scheduledRunAt || '').trim();
    if (!text || !runAt) return;
    setScheduling(true);
    try {
      await client.post('/api/ai/scheduled-questions', { questionText: text, runAt: new Date(runAt).toISOString() }, { timeout: 10000 });
      setScheduledQuestionText('');
      setScheduledRunAt('');
      const r = await client.get('/api/ai/scheduled-questions', { timeout: 8000 });
      setScheduledList(r.data?.items ?? []);
      setAnnouncer(t('scheduledSaved'));
    } catch (err) {
      setAnnouncer(toErrorString(err?.response?.data?.error ?? err?.message) || t('exportFailed'));
    } finally {
      setScheduling(false);
    }
  }, [scheduledQuestionText, scheduledRunAt, t]);

  const handleDeleteScheduled = useCallback(async (id) => {
    try {
      await client.delete(`/api/ai/scheduled-questions/${id}`, { timeout: 8000 });
      setScheduledList((prev) => prev.filter((s) => s.id !== id));
      setAnnouncer(t('scheduledDeleted'));
    } catch (_) {}
  }, [t]);

  const usagePct = headerUsage?.limitTokens > 0
    ? Math.round((100 * ((headerUsage.tokensIn ?? 0) + (headerUsage.tokensOut ?? 0))) / headerUsage.limitTokens)
    : null;
  const showUsageWarning = usagePct != null && usagePct >= 85;

  return (
    <div className="console-page" dir={dir} lang={lang}>
      <a href="#console-main" className="console-skip-link">
        {t('skipToMainContent')}
      </a>
      <ConfirmModal
        open={confirmNewChatOpen}
        title={t('confirmNewChatTitle')}
        message={t('confirmNewChatMessage')}
        confirmLabel={t('newChat')}
        cancelLabel={t('cancel')}
        onConfirm={doNewChat}
        onCancel={() => setConfirmNewChatOpen(false)}
      />
      <ConfirmModal
        open={confirmLogoutOpen}
        title={t('confirmLogoutTitle')}
        message={t('confirmLogoutMessage')}
        confirmLabel={t('logOut')}
        cancelLabel={t('cancel')}
        onConfirm={() => { logout(); setConfirmLogoutOpen(false); }}
        onCancel={() => setConfirmLogoutOpen(false)}
        danger
      />
      <SupportTicketModal
        open={showSupportTicketModal}
        onClose={() => setShowSupportTicketModal(false)}
        t={t}
        dir={dir}
      />
      {offline && (
        <div className="console-offline" role="status">
          {t('offlineMessage')}
        </div>
      )}
      <header className="console-header">
        <div className="console-header-inner">
          <div className="console-header-start">
            <div className="console-header-brand">
              <h1 className="console-title">
                {t('consoleTitle')} <span className="console-version-badge console-version-badge--desktop">{t('consoleVersion')}</span>
              </h1>
            </div>
            <nav className="console-header-nav console-header-nav--desktop" aria-label="Main">
            <button type="button" className="console-btn console-btn-new" onClick={handleNewChat}>
              {t('newChat')}
            </button>
            <button
              type="button"
              className={`console-btn ${showHistory ? 'console-btn-active' : ''}`}
              onClick={() => setShowHistory((v) => !v)}
            >
              {t('history')}
            </button>
            <button
              type="button"
              className={`console-btn ${showHelp ? 'console-btn-active' : ''}`}
              onClick={() => setShowHelp((v) => !v)}
              aria-expanded={showHelp}
              aria-label={t('help')}
              title={t('help')}
            >
              {t('help')}
            </button>
            <div className="console-export-wrap">
              <button
                type="button"
                className="console-btn console-btn-export"
                onClick={() => setExportMenuOpen((v) => !v)}
                disabled={messages.length === 0}
                aria-expanded={exportMenuOpen}
                aria-label={t('export')}
                title={t('export')}
              >
                {t('export')} ▾
              </button>
              {exportMenuOpen && (
                <>
                  <div className="console-export-backdrop" onClick={() => setExportMenuOpen(false)} aria-hidden />
                  <div className="console-export-dropdown" role="menu">
                    <button type="button" role="menuitem" className="console-export-item" onClick={() => handleCopyConversation()}>
                      {t('copyConversation')}
                    </button>
                    <button type="button" role="menuitem" className="console-export-item" onClick={() => handleExportPdf()}>
                      {t('exportPdf')}
                    </button>
                    <button type="button" role="menuitem" className="console-export-item" onClick={() => handleExportExcel()}>
                      {t('exportExcel')}
                    </button>
                  </div>
                </>
              )}
            </div>
            <button
              type="button"
              className={`console-btn ${showScheduled ? 'console-btn-active' : ''}`}
              onClick={() => setShowScheduled((v) => !v)}
              aria-expanded={showScheduled}
              title={t('scheduledQuestions')}
            >
              {t('scheduled')}
            </button>
            </nav>
          </div>

          <div className="console-header-actions console-header-actions--desktop">
            {usagePct != null && (
              <span className={`console-usage-pill ${showUsageWarning ? 'console-usage-pill--warning' : ''}`} title={t('usageUsed', { pct: usagePct })}>
                {t('usageUsed', { pct: usagePct })}
                {showUsageWarning && <span className="console-usage-pill-badge"> {t('usageNearLimit')}</span>}
              </span>
            )}
            <button
              type="button"
              className="console-btn console-settings-icon-btn"
              onClick={() => { setShowSettingsSidebar(true); hapticLight(); }}
              aria-label={t('settings')}
              title={t('providerSettingsTitle')}
            >
              <span className="console-settings-icon" aria-hidden>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </span>
              <span className="console-settings-icon-label">{t('settings')}</span>
            </button>
            <button
              type="button"
              className="console-lang-btn"
              onClick={() => setLocale(locale === 'en' ? 'ar' : 'en')}
              title={locale === 'en' ? t('langAr') : t('langEn')}
              aria-label={locale === 'en' ? t('langAr') : t('langEn')}
            >
              <span className="console-lang-switcher-icon" aria-hidden>🌐</span>
              {locale === 'en' ? 'ع' : 'EN'}
            </button>
            {branches.length > 1 && (
              <select
                className="console-branch-select"
                value={branchId}
                onChange={(e) => setBranchId(e.target.value || null)}
                aria-label={t('selectBranch')}
              >
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name_ar || b.name || `${t('branchDefault')} ${b.id}`}
                  </option>
                ))}
              </select>
            )}
            <span className="console-user">{user?.username || user?.full_name || user?.email || t('user')}</span>
            <button type="button" className="console-btn console-btn-logout" onClick={() => setConfirmLogoutOpen(true)}>
              {t('logOut')}
            </button>
          </div>

          <button
            ref={headerMenuBtnRef}
            type="button"
            className="console-header-menu-btn console-header-menu-btn--mobile"
            onClick={() => setHeaderMenuOpen((v) => !v)}
            aria-expanded={headerMenuOpen}
            aria-label={headerMenuOpen ? t('closeHistory') : t('menu')}
            aria-controls="console-header-menu"
          >
            <span className="console-header-menu-icon" aria-hidden>
              {headerMenuOpen ? '✕' : '☰'}
            </span>
          </button>
        </div>

        <div
          id="console-header-menu"
          className={`console-header-menu console-header-menu--mobile ${headerMenuOpen ? 'console-header-menu--open' : ''}`}
          role="menu"
          aria-hidden={!headerMenuOpen}
        >
          {headerMenuOpen && (
            <>
              <div
                className="console-header-menu-backdrop"
                onClick={() => setHeaderMenuOpen(false)}
                aria-hidden
              />
              <div ref={headerMenuPanelRef} className="console-header-menu-panel">
                <div className="console-header-menu-section">
                  <button
                    type="button"
                    className="console-header-menu-item"
                    onClick={() => { setShowSettingsSidebar(true); setHeaderMenuOpen(false); }}
                  >
                    {t('settings')}
                  </button>
                  <button
                    type="button"
                    className="console-header-menu-item console-header-menu-item--primary"
                    onClick={() => { handleNewChat(); setHeaderMenuOpen(false); }}
                  >
                    {t('newChat')}
                  </button>
                  <button
                    type="button"
                    className={`console-header-menu-item ${showHistory ? 'console-header-menu-item--active' : ''}`}
                    onClick={() => { setShowHistory((v) => !v); setHeaderMenuOpen(false); }}
                  >
                    {t('history')}
                  </button>
                  <button
                    type="button"
                    className={`console-header-menu-item ${showHelp ? 'console-header-menu-item--active' : ''}`}
                    onClick={() => { setShowHelp((v) => !v); setHeaderMenuOpen(false); }}
                  >
                    {t('help')}
                  </button>
                  <button
                    type="button"
                    className="console-header-menu-item"
                    disabled={messages.length === 0}
                    onClick={() => { handleCopyConversation(); setHeaderMenuOpen(false); }}
                  >
                    {t('copyConversation')}
                  </button>
                  <button
                    type="button"
                    className="console-header-menu-item"
                    disabled={messages.length === 0}
                    onClick={() => { handleExportPdf(); setHeaderMenuOpen(false); }}
                  >
                    {t('exportPdf')}
                  </button>
                  <button
                    type="button"
                    className="console-header-menu-item"
                    disabled={messages.length === 0}
                    onClick={() => { handleExportExcel(); setHeaderMenuOpen(false); }}
                  >
                    {t('exportExcel')}
                  </button>
                  <button
                    type="button"
                    className={`console-header-menu-item ${showScheduled ? 'console-header-menu-item--active' : ''}`}
                    onClick={() => { setShowScheduled((v) => !v); setHeaderMenuOpen(false); }}
                  >
                    {t('scheduled')}
                  </button>
                </div>
                <div className="console-header-menu-section">
                  <div className="console-header-menu-label">{t('language')}</div>
                  <button
                    type="button"
                    className="console-lang-btn console-header-menu-lang-btn"
                    onClick={() => setLocale(locale === 'en' ? 'ar' : 'en')}
                    aria-label={locale === 'en' ? t('langAr') : t('langEn')}
                  >
                    {locale === 'en' ? t('langAr') : t('langEn')}
                  </button>
                </div>
                {branches.length > 1 && (
                  <div className="console-header-menu-section">
                    <label className="console-header-menu-label" htmlFor="console-header-menu-branch">{t('selectBranch')}</label>
                    <select
                      id="console-header-menu-branch"
                      className="console-header-menu-select"
                      value={branchId}
                      onChange={(e) => setBranchId(e.target.value || null)}
                    >
                      {branches.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name_ar || b.name || `${t('branchDefault')} ${b.id}`}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="console-header-menu-section console-header-menu-section--user">
                  <span className="console-header-menu-user">{user?.username || user?.full_name || user?.email || t('user')}</span>
                  <button
                    type="button"
                    className="console-header-menu-item console-header-menu-item--logout"
                    onClick={() => { setConfirmLogoutOpen(true); setHeaderMenuOpen(false); }}
                  >
                    {t('logOut')}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </header>

      <div className={`console-body ${showHistory ? 'console-body--history-open' : ''} ${showHelp ? 'console-body--help-open' : ''} ${showScheduled ? 'console-body--scheduled-open' : ''} ${showSettingsSidebar ? 'console-body--settings-open' : ''} ${showPackingListModal ? 'console-body--settings-open' : ''}`}>
        {showScheduled && (
          <>
            <div className="console-history-backdrop" onClick={() => setShowScheduled(false)} onKeyDown={(e) => e.key === 'Escape' && setShowScheduled(false)} role="button" tabIndex={0} aria-label={t('scheduled')} />
            <aside className="console-history console-scheduled-panel" aria-label={t('scheduledQuestions')}>
              <div className="console-history-header">
                <h2>{t('scheduledQuestions')}</h2>
                <button type="button" className="console-btn console-btn-close" onClick={() => setShowScheduled(false)} aria-label={t('closeHistory')}>×</button>
              </div>
              <div className="console-scheduled-form">
                <input
                  type="text"
                  className="console-scheduled-input"
                  value={scheduledQuestionText}
                  onChange={(e) => setScheduledQuestionText(e.target.value)}
                  placeholder={t('scheduleQuestion')}
                  aria-label={t('scheduleQuestion')}
                />
                <input
                  type="datetime-local"
                  className="console-scheduled-input"
                  value={scheduledRunAt}
                  onChange={(e) => setScheduledRunAt(e.target.value)}
                  aria-label={t('runAt')}
                />
                <button type="button" className="console-btn console-btn-primary" onClick={handleScheduleQuestion} disabled={scheduling || !scheduledQuestionText.trim() || !scheduledRunAt.trim()}>
                  {scheduling ? t('saving') : t('scheduleQuestion')}
                </button>
              </div>
              <ul className="console-history-list">
                {scheduledList.map((s) => (
                  <li key={s.id} className="console-scheduled-item">
                    <span className="console-scheduled-item-text">{s.questionText || s.question_text}</span>
                    <span className="console-scheduled-item-run">{(s.runAt || s.run_at) ? new Date(s.runAt || s.run_at).toLocaleString(locale === 'ar' ? 'ar-SA' : undefined) : '—'}</span>
                    <button type="button" className="console-btn console-scheduled-delete" onClick={() => handleDeleteScheduled(s.id)} aria-label={t('cancel')}>×</button>
                  </li>
                ))}
              </ul>
            </aside>
          </>
        )}
        {showHelp && (
          <>
            <div className="console-history-backdrop" onClick={() => setShowHelp(false)} onKeyDown={(e) => e.key === 'Escape' && setShowHelp(false)} role="button" tabIndex={0} aria-label={t('closeHelp')} />
            <aside className="console-history console-help-panel" aria-label={t('help')}>
              <div className="console-history-header">
                <h2>{t('help')}</h2>
                <button type="button" className="console-btn console-btn-close" onClick={() => setShowHelp(false)} aria-label={t('closeHelp')}>×</button>
              </div>
              <div className="console-help-content">
                <p className="console-help-intro">{t('slashHelpIntro')}</p>
                <p className="console-help-commands"><code>{getSlashCommandListForHelp(locale === 'ar')}</code></p>
                <p className="console-help-hint">{t('slashHelpHint')}</p>
              </div>
            </aside>
          </>
        )}
        {showPackingListModal && (
          <PackingListFilesModal
            open={showPackingListModal}
            onClose={() => setShowPackingListModal(false)}
            onSelectFileId={(fileId) => {
              const sid = getOrCreateSessionId();
              const workspace = sessionWorkspaceRef.current[sid] || [];
              const name = `Packing list ${(fileId || '').slice(0, 8)}`;
              workspace.push({ fileId, type: 'packing_list', name, packingListFileId: fileId });
              sessionWorkspaceRef.current[sid] = workspace.slice(-10);
              setAttachments((a) => [...a, { fileId, type: 'packing_list', name, packingListFileId: fileId }]);
              setShowPackingListModal(false);
              setAnnouncer(t('fileAttached'));
            }}
            t={t}
            locale={locale}
          />
        )}
        {showSettingsSidebar && (
          <ProviderSettingsSidebar
            open={showSettingsSidebar}
            onClose={() => setShowSettingsSidebar(false)}
            t={t}
            dir={dir}
            creativeMode={creativeMode}
            onCreativeModeChange={setCreativeMode}
          />
        )}
        {showHistory && (
          <>
            <div
              className="console-history-backdrop"
              onClick={() => setShowHistory(false)}
              onKeyDown={(e) => e.key === 'Escape' && setShowHistory(false)}
              role="button"
              tabIndex={0}
              aria-label={t('closeHistory')}
            />
            <aside className="console-history">
            <div className="console-history-header">
              <h2>{t('conversations')}</h2>
              <button type="button" className="console-btn console-btn-close" onClick={() => setShowHistory(false)} aria-label={t('closeHistory')}>
                ×
              </button>
            </div>
            {sessionsLoading ? (
              <p className="console-history-loading">{t('loading')}</p>
            ) : sessions.length === 0 ? (
              <div className="console-history-empty">
                <p>{t('noPastConversations')}</p>
                <p className="console-history-empty-hint">{t('historyEmptyHint')}</p>
              </div>
            ) : (
              <ul className="console-history-list">
                {sessions.map((s) => (
                  <li key={s.sessionId}>
                    <button
                      type="button"
                      className="console-history-item"
                      onClick={() => loadConversation(s.sessionId)}
                    >
                      {s.updatedAt
                        ? new Date(s.updatedAt).toLocaleDateString(locale === 'ar' ? 'ar-SA' : undefined, {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : s.sessionId?.slice(0, 8)}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            </aside>
          </>
        )}

        <main id="console-main" className="console-main" tabIndex={-1}>
          <div
            ref={messagesScrollRef}
            className="console-messages"
          >
            {messages.length === 0 && (
              <div className="console-welcome">
                {/* Hero logo */}
                <div className="console-hero-logo">
                  <span className="console-hero-ring" aria-hidden="true" />
                  <div className="console-hero-logo-inner">
                    <img
                      src="/logo192.png"
                      alt="ORION"
                      className="console-hero-logo-img"
                      width={56}
                      height={56}
                      aria-hidden="true"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                </div>

                {/* Hero title */}
                <h2 className="console-hero-title">ORION</h2>
                <p className="console-hero-subtitle">{t('consoleSubtitle') || 'Financial Intelligence Engine'}</p>

                <p className="console-welcome-hint">{t('welcomeHint')}</p>

                {t('welcomeNew') && (
                  <div className="console-welcome-new">
                    {t('welcomeNew')}
                  </div>
                )}

                <div className="console-suggested">
                  {SUGGESTED_PROMPTS.map((s, i) => (
                    <button
                      key={i}
                      type="button"
                      className="console-suggested-btn"
                      onClick={() => handleSuggestedPrompt(s.prompt)}
                      disabled={loading}
                    >
                      {t(s.labelKey)}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`console-message console-message-${msg.role}`}
                data-role={msg.role}
              >
                <div className="console-message-label">
                  {msg.role === 'user' ? t('you') : t('orion')}
                </div>
                <div className="console-message-body">
                  {msg.role === 'assistant' ? (
                    <>
                      <div className="console-message-actions">
                        <button
                          type="button"
                          className="console-copy-btn"
                          onClick={() => {
                            const text = msg.oip?.type === 'chat' ? (msg.text || msg.oip?.message || '') : (msg.text || '');
                            if (text && navigator.clipboard?.writeText) {
                              navigator.clipboard.writeText(text);
                              setAnnouncer(t('copied'));
                            }
                          }}
                          aria-label={t('copy')}
                          title={t('copyMarkdown')}
                        >
                          <span className="console-copy-icon" aria-hidden>⎘</span>
                          <span className="console-copy-label">{t('copy')}</span>
                        </button>
                        <button
                          type="button"
                          className="console-copy-btn"
                          onClick={() => {
                            const plain = markdownToPlain(msg.oip?.type === 'chat' ? (msg.text || msg.oip?.message || '') : (msg.text || ''));
                            if (plain && navigator.clipboard?.writeText) {
                              navigator.clipboard.writeText(plain);
                              setAnnouncer(t('copiedPlain'));
                            }
                          }}
                          aria-label={t('copyPlain')}
                          title={t('copyPlain')}
                        >
                          <span className="console-copy-plain-label">T</span>
                        </button>
                      </div>
                      {msg.oip ? (
                        <OrionMessageRenderer
                          oip={msg.oip}
                          language={locale}
                          isRtl={dir === 'rtl'}
                          onApprove={(executionId, decision) => handleOipApprove(i, executionId, decision)}
                          disabled={loading}
                        />
                      ) : msg.text ? (
                        isLikelyLeakedJson(msg.text) ? (
                          <span className="console-no-response">{t('responseCouldNotBeDisplayed')}</span>
                        ) : (
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
                        )
                      ) : (
                        <span className="console-no-response">{t('noResponse')}</span>
                      )}
                      {Array.isArray(msg.toolsUsed) && msg.toolsUsed.length > 0 && (
                        <div className="console-tools-used" aria-label={t('toolsUsedLabel')}>
                          <span className="console-tools-used-icon" aria-hidden>⚡</span>
                          {msg.toolsUsed.map((t, idx) => (
                            <span key={`${t}-${idx}`} className="console-tools-used-chip">{toToolLabel(t)}</span>
                          ))}
                        </div>
                      )}
                      {msg.basedOn && (
                        <div className="console-based-on-wrap">
                          <button
                            type="button"
                            className="console-based-on"
                            onClick={() => setOpenBasedOnIndex(openBasedOnIndex === i ? null : i)}
                            aria-expanded={openBasedOnIndex === i}
                          >
                            <span className="console-based-on-text">
                              {t('basedOn')} {msg.basedOn.branchName || t('allBranches')} • {msg.basedOn.dateRange?.from || '—'} → {msg.basedOn.dateRange?.to || '—'}
                            </span>
                            <span className="console-based-on-icon" aria-hidden>{openBasedOnIndex === i ? '▼' : '▶'}</span>
                          </button>
                          {openBasedOnIndex === i && msg.basedOn && (
                            <div className="console-based-on-drawer" role="region" aria-label={t('basedOn')}>
                              {msg.basedOn.dateRange && (
                                <div className="console-based-on-row">
                                  <span>{locale === 'ar' ? 'النطاق الزمني' : 'Date range'}</span>
                                  <span>{msg.basedOn.dateRange.from && msg.basedOn.dateRange.to ? `${msg.basedOn.dateRange.from} – ${msg.basedOn.dateRange.to}` : '—'}</span>
                                </div>
                              )}
                              {msg.basedOn.dataPoints && typeof msg.basedOn.dataPoints === 'object' && (
                                <div className="console-based-on-section">
                                  <span className="console-based-on-section-title">{locale === 'ar' ? 'نقاط البيانات' : 'Data points'}</span>
                                  <div className="console-based-on-grid">
                                    {Object.entries(msg.basedOn.dataPoints).map(([k, v]) => (
                                      <div key={k} className="console-based-on-row">
                                        <span>{k}</span>
                                        <span>{(v ?? 0).toLocaleString()}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      {msg.toolResults?.length > 0 && (
                        <ToolResults
                          toolResults={msg.toolResults}
                          t={t}
                          locale={locale}
                          onCopy={() => setAnnouncer(t('copied'))}
                        />
                      )}
                      {msg.quickReplies?.length > 0 && (
                        <OrionQuickReplies
                          quickReplies={msg.quickReplies}
                          isRtl={dir === 'rtl'}
                          onQuickReply={handleQuickReply}
                          disabled={loading}
                          loadingQuickReplyId={loadingQuickReplyId}
                        />
                      )}
                      {msg.approvalPayload?.status === 'pending' && (
                        <div className="console-approval">
                          <div className="console-approval-summary">
                            {typeof msg.approvalPayload.preview === 'string'
                              ? msg.approvalPayload.preview
                              : msg.approvalPayload.preview?.summary ?? t('actionRequiresApprovalShort')}
                          </div>
                          <div className="console-approval-actions">
                            <button
                              type="button"
                              className="console-btn console-approval-btn console-approval-btn-approve"
                              onClick={() => executeApproved({ toolName: msg.approvalPayload.toolName, args: msg.approvalPayload.args }, { msgIndex: i })}
                              disabled={loading}
                            >
                              {t('approve')}
                            </button>
                            <button
                              type="button"
                              className="console-btn console-approval-btn console-approval-btn-cancel"
                              onClick={() => {
                                setMessages((prev) => {
                                  const next = [...prev];
                                  next[i] = { ...next[i], approvalPayload: { ...next[i].approvalPayload, status: 'cancelled' } };
                                  next.push({ role: 'assistant', text: t('operationCancelled') });
                                  return next;
                                });
                              }}
                              disabled={loading}
                            >
                              {t('cancel')}
                            </button>
                          </div>
                        </div>
                      )}
                      {msg.duplicatePayload?.messages && (
                        <div className="console-approval">
                          <div className="console-approval-summary">{t('duplicateDetected')}</div>
                          <div className="console-approval-actions">
                            <button
                              type="button"
                              className="console-btn console-approval-btn console-approval-btn-approve"
                              onClick={() => retryDuplicateAsk(msg.duplicatePayload)}
                              disabled={loading}
                            >
                              {t('runAgain')}
                            </button>
                            <button
                              type="button"
                              className="console-btn console-approval-btn console-approval-btn-cancel"
                              onClick={() => {
                                setMessages((prev) => [...prev, { role: 'assistant', text: t('okNotRunningAgain') }]);
                              }}
                              disabled={loading}
                            >
                              {t('cancel')}
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    msg.text
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="console-message console-message-assistant" data-role="assistant">
                <div className="console-message-label">{t('orion')}</div>
                <div className="console-message-body console-typing" aria-live="polite">
                  <span className="console-typing-dot" />
                  <span className="console-typing-dot" />
                  <span className="console-typing-dot" />
                  <span className="console-typing-status">{t('orionThinking')}</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {showScrollFab && (
            <button
              type="button"
              className="console-scroll-fab"
              onClick={scrollToBottom}
              aria-label={t('scrollToBottom')}
            >
              ↓
            </button>
          )}

          <div className="console-announcer" aria-live="polite" aria-atomic="true">
            {announcer}
          </div>

          {error && (
            <div className="console-error-wrap">
              <div className="console-error" role="alert">
                <span className="console-error-text">
                  {error === t('aiLimitReached') ? (
                    <>
                      {t('aiLimitReached')}
                      {' '}
                      <button
                        type="button"
                        className="console-error-support-btn"
                        onClick={() => setShowSupportTicketModal(true)}
                      >
                        {t('aiLimitReachedOpenTicket')}
                      </button>
                    </>
                  ) : (
                    toErrorString(error)
                  )}
                </span>
                {lastFailedPayload && (
                  <button
                    type="button"
                    className="console-error-retry"
                    onClick={() => {
                      setError(null);
                      setLastFailedPayload(null);
                      sendAsk({ text: lastFailedPayload.text });
                    }}
                  >
                    {t('retry')}
                  </button>
                )}
              </div>
            </div>
          )}
          {hasNewMessageBelow && !showScrollFab && (
            <button
              type="button"
              className="console-new-message-badge"
              onClick={() => { scrollToBottom(); setHasNewMessageBelow(false); }}
            >
              {t('newMessage')}
            </button>
          )}

          <form className="console-input-wrap" onSubmit={(e) => handleSend(e)}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,text/csv,text/plain,application/pdf,.xlsx,.xls,.sql"
              className="console-file-input"
              aria-hidden
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                e.target.value = '';
                const MAX_SIZE = 100 * 1024 * 1024; // 100 MB
                if (file.size > MAX_SIZE) {
                  setError(t('fileTooLarge'));
                  return;
                }
                setUploadingFile(true);
                try {
                  const fd = new FormData();
                  fd.append('file', file);
                  // Do not set Content-Type: let the browser set multipart/form-data with boundary
                  const res = await client.post('/api/ai/upload', fd, {
                    headers: { 'Content-Type': undefined },
                    timeout: 60000,
                  });
                  if (res.data?.success) {
                    setAttachments((a) => [...a, res.data]);
                    setAnnouncer(t('fileAttached'));
                  } else {
                    setError(toErrorString(res.data?.error ?? res.data) || t('uploadFailed'));
                  }
                } catch (err) {
                  setError(toErrorString(err?.response?.data?.error ?? err?.response?.data) || t('uploadFailed'));
                } finally {
                  setUploadingFile(false);
                }
              }}
            />
            <div className="console-composer">
              {attachments.length > 0 && (
                <div className="console-composer-attachments">
                  <span className="console-composer-attachments-label">{t('attachmentsLabel')}</span>
                  <div className="console-composer-chips">
                    {attachments.map((att) => (
                      <span key={att.fileId} className="console-composer-chip">
                        <span className="console-composer-chip-name">{att.name}</span>
                        <button
                          type="button"
                          className="console-composer-chip-remove"
                          onClick={() => setAttachments((a) => a.filter((x) => x.fileId !== att.fileId))}
                          aria-label={t('removeAttachment')}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="console-composer-row">
                <div className="console-composer-toolbar">
                  <button
                    type="button"
                    className="console-composer-btn console-composer-btn-attach"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={loading || uploadingFile}
                    aria-label={t('attachFile')}
                    title={t('attachFileTitle')}
                  >
                    {uploadingFile ? (
                      <span className="console-composer-btn-spinner" aria-hidden />
                    ) : (
                      <span className="console-composer-btn-icon console-composer-icon-attach" aria-hidden>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                        </svg>
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    className="console-composer-btn"
                    onClick={() => setShowPackingListModal(true)}
                    disabled={loading}
                    aria-label={t('packingListFiles')}
                    title={t('packingListFiles')}
                  >
                    <span className="console-composer-btn-icon" aria-hidden>📋</span>
                  </button>
                  {typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition) && (
                    <button
                      type="button"
                      className={`console-composer-btn console-composer-btn-mic ${listening ? 'console-composer-btn-mic--active' : ''}`}
                      onClick={() => {
                        const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
                        if (!SpeechRecognitionAPI) return;
                        if (listening) {
                          if (window.__orionSpeechRecognition) {
                            try { window.__orionSpeechRecognition.stop(); } catch (_) {}
                          }
                          setListening(false);
                          return;
                        }
                        const rec = new SpeechRecognitionAPI();
                        rec.continuous = true;
                        rec.interimResults = true;
                        rec.lang = locale === 'ar' ? 'ar-SA' : (navigator.language || 'en-US');
                        let finalTranscript = '';
                        rec.onresult = (e) => {
                          let interim = '';
                          for (let i = e.resultIndex; i < e.results.length; i++) {
                            const tr = e.results[i][0].transcript;
                            if (e.results[i].isFinal) finalTranscript += tr;
                            else interim += tr;
                          }
                          const text = (finalTranscript + interim).trim();
                          if (text) setInput((prev) => (prev ? `${prev} ${text}` : text));
                        };
                        rec.onend = () => setListening(false);
                        rec.onerror = () => setListening(false);
                        window.__orionSpeechRecognition = rec;
                        try {
                          rec.start();
                          setListening(true);
                        } catch (err) {
                          setAnnouncer(t('microphoneUnavailable'));
                        }
                      }}
                      disabled={loading}
                      aria-label={t('voiceInput')}
                      title={listening ? t('voiceInputStop') : t('voiceInput')}
                    >
                      <span className="console-composer-btn-icon" aria-hidden>🎤</span>
                    </button>
                  )}
                </div>
                <textarea
                  ref={inputRef}
                  className="console-composer-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend(e);
                    }
                  }}
                  placeholder={t('placeholderSendSlash') || getPlaceholderSlashHint(locale === 'ar')}
                  rows={2}
                  disabled={loading}
                  aria-label={t('messageOrion')}
                />
                <button
                  type="submit"
                  className="console-composer-send"
                  disabled={loading || (!input.trim() && attachments.length === 0)}
                  aria-label={t('send')}
                >
                  <span className="console-composer-send-text">{t('send')}</span>
                  <span className="console-composer-send-arrow" aria-hidden>↑</span>
                </button>
              </div>
              <p className="console-composer-hint">{t('welcomeHint')}</p>
            </div>
          </form>
        </main>
      </div>
    </div>
  );
}
